import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { X, Sparkles, Globe, Target, Mail } from 'lucide-react';

export const ReadingForm = ({ isOpen, onClose, onSubmit }) => {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [email, setEmail] = useState('');
  const [monthlyOptIn, setMonthlyOptIn] = useState(false);

  if (!isOpen) return null;

  const canSubmit = websiteUrl.trim().length > 0 && question.trim().length > 0 && email.trim().length > 0 && email.includes('@');

  const handleSubmit = () => {
    if (monthlyOptIn && email.trim()) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      }).catch(() => {});
    }
    onSubmit({
      goal: question.trim(),
      audience: '',
      industry: '',
      companySize: '',
      segment: '',
      challenge: question.trim(),
      websiteUrl: websiteUrl.trim(),
      email: email.trim() || null,
    });
    setWebsiteUrl('');
    setQuestion('');
    setEmail('');
    setMonthlyOptIn(false);
  };

  const handleClose = () => {
    setWebsiteUrl('');
    setQuestion('');
    setEmail('');
    setMonthlyOptIn(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[#0a1628]/90 backdrop-blur-md"
        onClick={handleClose}
      />

      <div className={cn(
        "relative w-full max-w-lg",
        "bg-gradient-to-br from-[#1e2a4a] to-[#141c33]",
        "border-2 border-[#d4c4a8]/40 rounded-2xl",
        "shadow-2xl shadow-[#e89b47]/10",
        "animate-scale-in"
      )}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#d4c4a8]/60 hover:text-[#d4c4a8] transition-colors z-10"
          data-testid="reading-form-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="h-2 bg-gradient-to-r from-transparent via-[#e89b47] to-transparent" />

        <div className="p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center",
                "bg-gradient-to-br from-[#e89b47]/20 to-[#e89b47]/5",
                "border-2 border-[#e89b47]/40"
              )}>
                <Sparkles className="w-8 h-8 text-[#e89b47]" />
              </div>
            </div>
            <h2 className="font-display text-2xl text-[#e8dcc8] mb-2">
              Start Your Reading
            </h2>
            <p className="font-body text-[#d4c4a8]/60 text-sm">
              We'll analyze your company and deliver personalized insights
            </p>
          </div>

          <div className="space-y-6">
            {/* Company Website URL */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Globe className="w-4 h-4 text-[#e89b47]" />
                Company Website URL
              </label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://yourcompany.com"
                className={cn(
                  "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                  "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                  "font-body text-sm",
                  "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                  "transition-all duration-300"
                )}
                data-testid="reading-website-url"
              />
            </div>

            {/* Ask a Question */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Target className="w-4 h-4 text-[#e89b47]" />
                Ask a Question
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="What do you need clarity on — brand, growth, content, audience, or direction?"
                rows={3}
                className={cn(
                  "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                  "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                  "font-body text-sm resize-none",
                  "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                  "transition-all duration-300"
                )}
                data-testid="reading-question"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Mail className="w-4 h-4 text-[#e89b47]" />
                Enter your email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={cn(
                  "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                  "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                  "font-body text-sm",
                  "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                  "transition-all duration-300"
                )}
                data-testid="reading-email"
              />
            </div>

            {/* Monthly reminder opt-in */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={monthlyOptIn}
                onChange={(e) => setMonthlyOptIn(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#e89b47] cursor-pointer"
                data-testid="reading-reminder-optin"
              />
              <span className="font-body text-xs text-[#d4c4a8]/60 leading-relaxed">
                Email me a monthly reminder to draw a fresh reading (unsubscribe anytime)
              </span>
            </label>

            {/* Submit */}
            <div className="flex justify-center pt-4">
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={cn(
                  "font-display tracking-wide px-10 py-6 text-lg transition-all",
                  canSubmit
                    ? "bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 shadow-lg shadow-[#e89b47]/20"
                    : "bg-[#d4c4a8]/20 text-[#d4c4a8]/50 cursor-not-allowed"
                )}
                data-testid="reading-submit-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Reveal My Reading
              </Button>
            </div>
          </div>
        </div>

        <div className="h-1 bg-gradient-to-r from-transparent via-[#e89b47]/50 to-transparent" />
      </div>
    </div>
  );
};

export default ReadingForm;
